import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePrematchEvents, useLiveEvents } from '../services/sportsService';
import { SportsEvent, getSportIcon } from '../types/sports';
import SportIcon from './sports/SportIcon';
import { formatEventTime } from '../services/sportsService';
import OddsWithMovement from './sports/OddsWithMovement';

const FamousLeagues: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Fetch popular sports events (top leagues)
  const { data: sportsEventsData, isLoading: sportsLoading } = usePrematchEvents({
    sportType: 'sport',
    page: 1,
    perPage: 30,
  });

  // Fetch live sports events
  const { data: liveSportsData, isLoading: liveSportsLoading } = useLiveEvents({
    sportType: 'sport',
    page: 1,
    perPage: 20,
  });

  // Fetch popular esports events
  const { data: esportsEventsData, isLoading: esportsLoading } = usePrematchEvents({
    sportType: 'esport',
    page: 1,
    perPage: 30,
  });

  // Fetch live esports events
  const { data: liveEsportsData, isLoading: liveEsportsLoading } = useLiveEvents({
    sportType: 'esport',
    page: 1,
    perPage: 20,
  });

  const isLoading = sportsLoading || liveSportsLoading || esportsLoading || liveEsportsLoading;

  // Get featured events from different leagues - ONLY show real live events
  const featuredEvents = useMemo(() => {
    const events: SportsEvent[] = [];
    const maxEvents = 10; // Show 10 popular events
    
    // Helper function to check if event is actually live
    const isActuallyLive = (event: SportsEvent): boolean => {
      return event.gameType === 'live' && 
             event.status?.isLive === true && 
             event.status?.isStarted === true;
    };
    
    // Add ONLY real live sports events (must be actually live)
    if (liveSportsData?.groupedEvents && Array.isArray(liveSportsData.groupedEvents)) {
      liveSportsData.groupedEvents.forEach((location: any) => {
        if (location?.leagues && Array.isArray(location.leagues)) {
          location.leagues.forEach((league: any) => {
            if (league?.events && Array.isArray(league.events)) {
              league.events.forEach((event: SportsEvent) => {
                // Only add if actually live and not already added
                if (events.length < maxEvents && 
                    event?.fixtureId && 
                    isActuallyLive(event) &&
                    !events.find(e => e.fixtureId === event.fixtureId)) {
                  events.push(event);
                }
              });
            }
          });
        }
      });
    }

    // Add ONLY real live esports events (must be actually live)
    if (liveEsportsData?.groupedEvents && Array.isArray(liveEsportsData.groupedEvents) && events.length < maxEvents) {
      liveEsportsData.groupedEvents.forEach((location: any) => {
        if (location?.leagues && Array.isArray(location.leagues)) {
          location.leagues.forEach((league: any) => {
            if (league?.events && Array.isArray(league.events)) {
              league.events.forEach((event: SportsEvent) => {
                // Only add if actually live and not already added
                if (events.length < maxEvents && 
                    event?.fixtureId && 
                    isActuallyLive(event) &&
                    !events.find(e => e.fixtureId === event.fixtureId)) {
                  events.push(event);
                }
              });
            }
          });
        }
      });
    }

    // Add upcoming sports events (only if we don't have enough live events)
    if (sportsEventsData?.groupedEvents && Array.isArray(sportsEventsData.groupedEvents) && events.length < maxEvents) {
      sportsEventsData.groupedEvents.forEach((location: any) => {
        if (location?.leagues && Array.isArray(location.leagues)) {
          location.leagues.forEach((league: any) => {
            if (league?.events && Array.isArray(league.events)) {
              league.events.forEach((event: SportsEvent) => {
                // Only add prematch events (not live)
                if (events.length < maxEvents && 
                    event?.fixtureId && 
                    event.gameType === 'prematch' &&
                    !events.find(e => e.fixtureId === event.fixtureId)) {
                  events.push(event);
                }
              });
            }
          });
        }
      });
    }

    // Add upcoming esports events (only if we don't have enough events)
    if (esportsEventsData?.groupedEvents && Array.isArray(esportsEventsData.groupedEvents) && events.length < maxEvents) {
      esportsEventsData.groupedEvents.forEach((location: any) => {
        if (location?.leagues && Array.isArray(location.leagues)) {
          location.leagues.forEach((league: any) => {
            if (league?.events && Array.isArray(league.events)) {
              league.events.forEach((event: SportsEvent) => {
                // Only add prematch events (not live)
                if (events.length < maxEvents && 
                    event?.fixtureId && 
                    event.gameType === 'prematch' &&
                    !events.find(e => e.fixtureId === event.fixtureId)) {
                  events.push(event);
                }
              });
            }
          });
        }
      });
    }

    return events.slice(0, maxEvents);
  }, [sportsEventsData, liveSportsData, esportsEventsData, liveEsportsData]);

  // Scroll functionality
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        ref.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [featuredEvents]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 600;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Show loading state with placeholders
  if (isLoading) {
    return (
      <div className="py-6 sm:py-8 section-gradient w-full overflow-hidden">
        <div className="w-full max-w-[1800px] mx-auto px-3 sm:px-4">
          {/* Header placeholder */}
          <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-5">
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
              <div className="w-6 h-6 sm:w-8 md:w-10 bg-dark-700 rounded animate-pulse"></div>
              <div className="h-5 sm:h-6 md:h-8 bg-dark-700 rounded-lg w-32 sm:w-40 md:w-48 animate-pulse"></div>
              <div className="hidden md:block h-px bg-dark-700 w-12 lg:w-24"></div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="h-8 sm:h-9 md:h-10 bg-dark-700 rounded-lg sm:rounded-xl w-16 sm:w-20 md:w-24 animate-pulse"></div>
              <div className="hidden sm:flex items-center gap-1">
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-dark-700 rounded-lg sm:rounded-xl animate-pulse"></div>
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-dark-700 rounded-lg sm:rounded-xl animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Cards placeholder */}
          <div className="flex gap-2 sm:gap-3 md:gap-4 overflow-hidden">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(j => (
              <div key={j} className="flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px]">
                <div className="bg-dark-700 rounded-lg sm:rounded-xl md:rounded-2xl p-4 sm:p-5 animate-pulse">
                  <div className="h-4 bg-dark-600 rounded mb-3"></div>
                  <div className="h-6 bg-dark-600 rounded mb-2"></div>
                  <div className="h-4 bg-dark-600 rounded w-12 mx-auto mb-2"></div>
                  <div className="h-6 bg-dark-600 rounded mb-3"></div>
                  <div className="flex gap-2 mb-3">
                    <div className="flex-1 h-10 bg-dark-600 rounded"></div>
                    <div className="flex-1 h-10 bg-dark-600 rounded"></div>
                    <div className="flex-1 h-10 bg-dark-600 rounded"></div>
                  </div>
                  <div className="h-4 bg-dark-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Don't show section if no events
  if (featuredEvents.length === 0) {
    return null;
  }

  return (
    <div className="py-6 sm:py-8 section-gradient w-full overflow-hidden">
      <div className="w-full max-w-[1800px] mx-auto px-3 sm:px-4">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-5">
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
            <span className="text-lg sm:text-xl md:text-2xl flex-shrink-0">⚽</span>
            <h2 className="text-white text-sm sm:text-lg md:text-xl lg:text-2xl font-bold truncate">
              {t('famousLeagues.heading', { defaultValue: 'Famous Leagues' })}
            </h2>
            <div className="hidden md:block h-px bg-gradient-to-r from-white/10 to-transparent w-12 lg:w-24 flex-shrink-0"></div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/sports')}
              className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-dark-700/80 hover:bg-primary-500/20 text-gray-300 hover:text-primary-400 text-[10px] sm:text-xs md:text-sm font-medium rounded-lg sm:rounded-xl transition-all duration-200 border border-white/5 hover:border-primary-500/30 whitespace-nowrap"
            >
              View All
            </button>
            <div className="hidden sm:flex items-center gap-1">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-200 ${
                  canScrollLeft 
                    ? 'bg-dark-700/80 hover:bg-dark-600 text-white border border-white/5 hover:border-white/10' 
                    : 'bg-dark-800 text-gray-600 cursor-not-allowed border border-white/5'
                }`}
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-200 ${
                  canScrollRight 
                    ? 'bg-dark-700/80 hover:bg-dark-600 text-white border border-white/5 hover:border-white/10' 
                    : 'bg-dark-800 text-gray-600 cursor-not-allowed border border-white/5'
                }`}
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Events Row - Horizontal Scrollable */}
        <div
          ref={scrollRef}
          className="flex gap-2 sm:gap-3 md:gap-4 overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {featuredEvents.map((event, index) => {
            const homeTeam = event.participants?.[0]?.name || 'Home';
            const awayTeam = event.participants?.[1]?.name || 'Away';
            const leagueName = event.league?.name || event.league?.nameKor || 'League';
            const sportIcon = getSportIcon(event.sport?.oddsId || 0, event.sport?.name || '');
            // Only show as live if it's actually live (not just gameType)
            const isLive = event.gameType === 'live' && event.status?.isLive === true && event.status?.isStarted === true;
            const homeScore = event.liveScore?.home;
            const awayScore = event.liveScore?.away;
            
            // Color gradients based on index for variety
            const gradients = [
              'from-emerald-500/20 via-green-500/10 to-emerald-600/20',
              'from-blue-500/20 via-cyan-500/10 to-blue-600/20',
              'from-purple-500/20 via-pink-500/10 to-purple-600/20',
              'from-orange-500/20 via-red-500/10 to-orange-600/20',
              'from-yellow-500/20 via-amber-500/10 to-yellow-600/20',
              'from-indigo-500/20 via-violet-500/10 to-indigo-600/20',
            ];
            const gradient = gradients[index % gradients.length];
            const borderGradients = [
              'border-emerald-500/30 hover:border-emerald-400/50',
              'border-blue-500/30 hover:border-blue-400/50',
              'border-purple-500/30 hover:border-purple-400/50',
              'border-orange-500/30 hover:border-orange-400/50',
              'border-yellow-500/30 hover:border-yellow-400/50',
              'border-indigo-500/30 hover:border-indigo-400/50',
            ];
            const borderGradient = borderGradients[index % borderGradients.length];

            return (
              <div
                key={event.fixtureId}
                onClick={() => navigate(`/event/${event.fixtureId}?type=${isLive ? 'live' : 'prematch'}`)}
                className="flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px] cursor-pointer group"
              >
                <div className={`relative overflow-hidden bg-gradient-to-br ${gradient} border ${borderGradient} rounded-lg sm:rounded-xl md:rounded-2xl p-4 sm:p-5 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] backdrop-blur-sm h-full`}>
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full blur-2xl"></div>
                </div>

                {/* Live Badge */}
                {isLive && (
                  <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-500 border border-red-400/50 rounded-full shadow-lg shadow-red-500/30">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    <span className="text-white text-xs font-bold">LIVE</span>
                  </div>
                )}

                {/* League Info with gradient */}
                <div className="relative z-10 flex items-center gap-2 mb-4 pb-3 border-b border-white/10 overflow-hidden">
                  <SportIcon icon={sportIcon} size="lg" className="flex-shrink-0" />
                  <span className="text-sm text-white font-semibold whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0" title={leagueName}>{leagueName}</span>
                  {!isLive && (
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                      {formatEventTime(event.startDate)}
                    </span>
                  )}
                </div>

                {/* Teams with scores - Vertical layout */}
                <div className="relative z-10 mb-4">
                  {/* Home Team */}
                  <div className="text-center mb-3">
                    <p className="text-white font-bold text-base sm:text-lg md:text-xl truncate px-2" title={homeTeam}>
                      {homeTeam}
                    </p>
                    
                  </div>

                  {/* VS or Score divider */}
                  <div className="flex items-center justify-center my-2 sm:my-3">
                    {isLive && homeScore !== undefined && awayScore !== undefined ? (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-2xl sm:text-3xl font-bold text-white">{homeScore}</span>
                        <span className="text-gray-400 text-base sm:text-lg">-</span>
                        <span className="text-2xl sm:text-3xl font-bold text-white">{awayScore}</span>
                      </div>
                    ) : (
                      <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 rounded-full">
                        <span className="text-gray-300 text-xs sm:text-sm font-semibold">VS</span>
                      </div>
                    )}
                  </div>

                  {/* Away Team */}
                  <div className="text-center">
                    <p className="text-white font-bold text-base sm:text-lg md:text-xl truncate px-2" title={awayTeam}>
                      {awayTeam}
                    </p>
                   
                  </div>
                </div>

                {/* Odds Preview */}
                {event.odds && (event.odds.home || event.odds.draw || event.odds.away) && (
                  <div className="relative z-10 flex gap-2 mb-4">
                    {event.odds.home && (
                      <div className="flex-1 px-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-center transition-colors">
                        <div className="text-[10px] text-gray-400 mb-0.5">1</div>
                        <div className="text-white font-bold text-sm">
                          <OddsWithMovement cellKey={`${event.fixtureId}-${(event.odds.home as any)?.betId ?? 'home'}`} odds={(event.odds.home as any)?.price} />
                        </div>
                      </div>
                    )}
                    {event.odds.draw && (
                      <div className="flex-1 px-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-center transition-colors">
                        <div className="text-[10px] text-gray-400 mb-0.5">X</div>
                        <div className="text-white font-bold text-sm">
                          <OddsWithMovement cellKey={`${event.fixtureId}-${(event.odds.draw as any)?.betId ?? 'draw'}`} odds={(event.odds.draw as any)?.price} />
                        </div>
                      </div>
                    )}
                    {event.odds.away && (
                      <div className="flex-1 px-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-center transition-colors">
                        <div className="text-[10px] text-gray-400 mb-0.5">2</div>
                        <div className="text-white font-bold text-sm">
                          <OddsWithMovement cellKey={`${event.fixtureId}-${(event.odds.away as any)?.betId ?? 'away'}`} odds={(event.odds.away as any)?.price} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Button */}
                <div className="relative z-10 flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-xs text-gray-300">
                    {isLive 
                      ? t('sports.live', { defaultValue: 'Live Now' })
                      : t('sports.upcoming', { defaultValue: 'Upcoming' })
                    }
                  </span>
                  <div className="flex items-center gap-1 text-emerald-300 group-hover:text-emerald-200 font-semibold text-sm transition-colors">
                    <span>{t('common.betNow', { defaultValue: 'Bet Now' })}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FamousLeagues;
